
import { defineComponent, PropType, computed } from 'vue';
import { IBienBanGiaiTheResponseBase } from '@/core/interfaces/ApiResponses';
import { useBienBanConfirmApprove } from '@/core/hooks/use-bien-ban-confirm-approve';
import { useButtonCustom } from '@/core/hooks/use-button-custom';
import { useBienBan } from '@/core/hooks/use-bien-ban-hook';
import usePermission from '@/core/hooks/usePermission';
import ButtonCustom from '@/components/shared/buttons/ButtonCustom.vue';
import ConfirmationApproveDialog from '@/components/confirmation-approve-dialog/ConfirmationApproveDialog.vue';
import PageHeaderActionItem from '@/layout/header/PageHeaderActionItem.vue';
import { faCheckToSlot } from '@fortawesome/free-solid-svg-icons';

export default defineComponent({
  name: 'BienBanApproveButton',

  components: {
    ButtonCustom,
    ConfirmationApproveDialog,
    PageHeaderActionItem,
  },

  props: {
    bienBan: {
      type: Object as PropType<IBienBanGiaiTheResponseBase | null>,
      required: true,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    tooltipMessage: {
      type: String,
      default: '',
    },
    classes: {
      type: String,
      default: '',
    },
    asMenuItem: {
      type: Boolean,
      default: false,
    },
    onSuccess: {
      type: Function as PropType<() => void | Promise<void>>,
      default: undefined,
    },
  },

  setup(props) {
    const { ButtonTypeColors, ButtonsType } = useButtonCustom();
    const { canDuyetBbgt } = usePermission();
    const { approvalLevel } = useBienBan();

    const {
      showApproveDialog,
      openApproveDialog: openDialog,
      closeApproveDialog,
      handleApprove: handleApproveAction,
    } = useBienBanConfirmApprove(props.onSuccess);

    const openApproveDialog = () => {
      if (props.bienBan) {
        openDialog(props.bienBan);
      }
    };

    const handleApprove = async () => {
      await handleApproveAction();
    };

    const computedTooltipMessage = computed(() => {
      if (props.tooltipMessage) {
        return props.tooltipMessage;
      }

      if (!props.bienBan) {
        return '';
      }

      if (!canDuyetBbgt.value) {
        return 'Không có quyền';
      }

      if (props.bienBan.is_admin_approved) {
        return 'Đã duyệt';
      }

      if (approvalLevel.value === 2 && !props.bienBan.is_approved) {
        return 'Phải xác nhận trước khi duyệt';
      }

      return 'GĐ Phê duyệt';
    });

    const computedIsDisabled = computed(() => {
      if (props.isDisabled) {
        return true;
      }

      if (!props.bienBan) {
        return true;
      }

      // Disable if already approved or no permission
      if (props.bienBan.is_admin_approved || !canDuyetBbgt.value) {
        return true;
      }

      // If approval level is 2, disable approve until confirm is done
      if (approvalLevel.value === 2 && !props.bienBan.is_approved) {
        return true;
      }

      return false;
    });

    return {
      ButtonTypeColors,
      ButtonsType,
      showApproveDialog,
      openApproveDialog,
      closeApproveDialog,
      handleApprove,
      computedTooltipMessage,
      computedIsDisabled,
      faCheckToSlot,
    };
  },
});
